/**
 * DosingMixingTrainPage.tsx
 * Full combined process train view:
 * Influent → Chemical Dosing Stage → Impeller Mixing Stage → Final Effluent
 * Reads from rb_chem_dosing_v1 and rb_impeller_mixing_v1 localStorage keys.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  FlaskConical, Waves, ArrowRight, ChevronRight, Beaker,
  CheckCircle2, AlertTriangle, Info, BarChart3,
  Edit3, LayoutDashboard,
} from 'lucide-react';
import { loadDosingState } from './ChemicalDosingPage';
import { loadMixingState } from './ImpellerMixingPage';

// Re-export impeller list for use here (we'll just re-define the lookup)
const IMPELLER_NAMES: Record<string, string> = {
  rushton6:   'Rushton 6-blade Disc Turbine',
  rushton4:   'Rushton 4-blade Disc Turbine',
  ptb45:      'Pitched-blade Turbine 45°',
  ptb60:      'Pitched-blade Turbine 60°',
  marine:     'Marine Propeller (3-blade)',
  hydrofoil:  'Hydrofoil A310',
  anchor:     'Anchor Paddle',
  gate:       'Gate/Frame Paddle',
  flatpaddle: 'Flat Paddle (2-blade)',
};

// ─── Param display meta ───────────────────────────────────────────────────────
interface ParamMeta { label: string; unit: string; decimals: number }
const PARAMS: Record<string, ParamMeta> = {
  pH:          { label: 'pH',              unit: '',        decimals: 2 },
  turbidity:   { label: 'Turbidity',       unit: 'NTU',    decimals: 1 },
  tss:         { label: 'TSS',             unit: 'mg/L',   decimals: 1 },
  cod:         { label: 'COD',             unit: 'mg/L',   decimals: 1 },
  bod:         { label: 'BOD',             unit: 'mg/L',   decimals: 1 },
  tn:          { label: 'TN',              unit: 'mg/L',   decimals: 1 },
  tp:          { label: 'TP',              unit: 'mg/L',   decimals: 2 },
  nh3:         { label: 'NH₃-N',           unit: 'mg/L',   decimals: 2 },
  tds:         { label: 'TDS',             unit: 'mg/L',   decimals: 0 },
  alkalinity:  { label: 'Alkalinity',      unit: 'mg/L',   decimals: 1 },
  hardness:    { label: 'Total Hardness',  unit: 'mg/L',   decimals: 1 },
  conductivity:{ label: 'Conductivity',    unit: 'µS/cm',  decimals: 0 },
  temperature: { label: 'Temperature',     unit: '°C',     decimals: 1 },
  oilgrease:   { label: 'Oil & Grease',    unit: 'mg/L',   decimals: 1 },
};

const fmt = (v: number | undefined, d = 1) =>
  v !== undefined && isFinite(v) && !isNaN(v) ? v.toFixed(d) : '—';

const fmtSI = (v: number) => {
  if (!isFinite(v) || isNaN(v)) return '—';
  if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'k';
  return v.toFixed(1);
};

// ─── Compute final effluent after mixing ─────────────────────────────────────
function calcMixingEffect(
  postDosingParams: Record<string, number>,
  G: number,
  Gt: number
): Record<string, number> {
  const out = { ...postDosingParams };

  // Turbidity + TSS removal via G-value / Gt
  let turbRemoval = 0;
  let tssRemoval  = 0;
  if (G >= 10 && Gt >= 1000) {
    turbRemoval = Math.min(95, 20 + 30 * Math.log10(Math.max(1, Gt / 1000)));
    tssRemoval  = turbRemoval * 0.9;
  }
  // High shear — re-suspension penalty
  if (G > 1500) {
    turbRemoval = Math.max(0, turbRemoval - (G - 1500) / 100);
    tssRemoval  = Math.max(0, tssRemoval  - (G - 1500) / 100);
  }
  out.turbidity = Math.max(0, (postDosingParams.turbidity ?? 0) * (1 - turbRemoval / 100));
  out.tss       = Math.max(0, (postDosingParams.tss       ?? 0) * (1 - tssRemoval  / 100));

  // COD slight removal from stripping/volatilisation in agitated tank
  const codRemoval = G > 100 ? Math.min(5, (G - 100) / 500 * 5) : 0;
  out.cod = Math.max(0, (postDosingParams.cod ?? 0) * (1 - codRemoval / 100));

  return out;
}

// ─── Removal efficiency ───────────────────────────────────────────────────────
function removalPct(inlet: number, outlet: number): number | null {
  if (inlet <= 0) return null;
  return Math.max(-999, Math.min(100, ((inlet - outlet) / inlet) * 100));
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ParamRow({ label, unit, inlet, postDosing, final, decimals = 1 }: {
  label: string; unit: string;
  inlet: number | undefined;
  postDosing: number | undefined;
  final: number | undefined;
  decimals?: number;
}) {
  const inletV    = inlet    ?? 0;
  const postV     = postDosing ?? 0;
  const finalV    = final    ?? 0;
  const removal   = removalPct(inletV, finalV);
  const isPositive = removal !== null && removal >= 0;
  const significant = removal !== null && Math.abs(removal) > 1;

  return (
    <tr className="border-b border-slate-800/60 hover:bg-slate-800/20 transition-colors">
      <td className="py-2 pl-3 pr-2 text-xs text-slate-300 font-medium whitespace-nowrap">
        {label}
        {unit && <span className="text-slate-500 ml-1 font-normal">({unit})</span>}
      </td>
      <td className="py-2 px-3 text-xs font-mono text-slate-300 text-right">{fmt(inletV, decimals)}</td>
      <td className="py-2 px-3 text-xs font-mono text-slate-300 text-right">{fmt(postV, decimals)}</td>
      <td className="py-2 px-3 text-xs font-mono text-slate-200 font-semibold text-right">{fmt(finalV, decimals)}</td>
      <td className="py-2 pr-3 text-xs text-right">
        {removal !== null && significant ? (
          <span className={`font-mono font-semibold ${
            isPositive ? 'text-emerald-400' : 'text-red-400'
          }`}>
            {isPositive ? '▼' : '▲'} {Math.abs(removal).toFixed(1)}%
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
    </tr>
  );
}

function StageCard({ title, icon, color, children }: {
  title: string; icon: React.ReactNode; color: string; children: React.ReactNode;
}) {
  const borders: Record<string, string> = {
    blue:   'border-blue-700/50 bg-blue-950/20',
    green:  'border-emerald-700/50 bg-emerald-950/20',
    amber:  'border-amber-700/50 bg-amber-950/20',
    purple: 'border-purple-700/50 bg-purple-950/20',
  };
  const headers: Record<string, string> = {
    blue:   'text-blue-300',
    green:  'text-emerald-300',
    amber:  'text-amber-300',
    purple: 'text-purple-300',
  };
  return (
    <div className={`rounded-2xl border p-4 ${borders[color] ?? borders.blue}`}>
      <div className={`flex items-center gap-2 mb-3 ${headers[color] ?? headers.blue}`}>
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      {children}
    </div>
  );
}

function KPIChip({ label, value, unit, color = 'slate' }: {
  label: string; value: string; unit: string; color?: string;
}) {
  const styles: Record<string, string> = {
    slate:  'bg-slate-800/60 border-slate-700/50 text-slate-300',
    amber:  'bg-amber-900/30 border-amber-700/40 text-amber-300',
    green:  'bg-emerald-900/30 border-emerald-700/40 text-emerald-300',
    red:    'bg-red-900/30 border-red-700/40 text-red-300',
    blue:   'bg-blue-900/30 border-blue-700/40 text-blue-300',
    purple: 'bg-purple-900/30 border-purple-700/40 text-purple-300',
  };
  return (
    <div className={`rounded-xl border p-3 ${styles[color] ?? styles.slate}`}>
      <div className="text-xs text-slate-400 mb-0.5">{label}</div>
      <div className="text-lg font-bold font-mono">
        {value} <span className="text-xs font-normal text-slate-400">{unit}</span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DosingMixingTrainPage() {
  const navigate = useNavigate();
  const dosingState  = useMemo(() => loadDosingState(),  []);
  const mixingState  = useMemo(() => loadMixingState(),  []);

  const hasDosingData  = !!dosingState;
  const hasMixingData  = !!mixingState;

  // Influent params
  const influentParams: Record<string, number> = dosingState?.influentParams  ?? {};
  const postDosingParams: Record<string, number> = dosingState?.postDosingParams ?? {};

  // Mixing results
  const mixResults  = mixingState?.results;
  const mixCfg      = mixingState?.config;

  // Compute final effluent
  const finalParams: Record<string, number> = useMemo(() => {
    if (!mixResults || Object.keys(postDosingParams).length === 0) return postDosingParams;
    return calcMixingEffect(postDosingParams, mixResults.G, mixResults.Gt);
  }, [postDosingParams, mixResults]);

  // Active chemicals
  const activeChemicals = dosingState?.chemicals.filter(c => c.enabled && c.dose > 0) ?? [];
  const flowQ = dosingState?.flowRateMH ?? 0;

  // Total chemical mass flow
  const totalKgh = activeChemicals.reduce((sum, c) => sum + (c.dose / 1000) * flowQ, 0);
  const totalKgd = totalKgh * 24;

  // Power intensity
  const powerDensity = mixResults && mixCfg
    ? mixResults.P_kW / mixCfg.tankVolume
    : 0;

  // Status
  const gColor = mixResults
    ? mixResults.G < 10 ? 'amber' : mixResults.G > 1500 ? 'red' : 'green'
    : 'slate';

  const gtColor = mixResults
    ? (mixResults.Gt > 1000 && mixResults.Gt <= 100000 ? 'green' : 'amber')
    : 'slate';

  const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-700/50">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-green-600 flex items-center justify-center">
              <LayoutDashboard className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-100 leading-none">Dosing & Mixing Process Train</h1>
              <div className="text-xs text-slate-400 mt-0.5">
                {dosingState
                  ? `${dosingState.influentSampleName} · Q = ${flowQ} m³/h · ${today}`
                  : `No session data · ${today}`}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate('/chemical-dosing')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-600 hover:border-slate-500 text-slate-300 text-xs transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit Dosing
            </button>
            <button
              onClick={() => navigate('/impeller-mixing')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-600 hover:border-slate-500 text-slate-300 text-xs transition-colors"
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit Mixing
            </button>
            <button
              onClick={() => navigate('/dosing')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-semibold transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Main Dosing
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 py-6 space-y-6">

        {/* ── No data warnings ─────────────────────────────────────────────── */}
        {!hasDosingData && (
          <div className="flex items-start gap-3 bg-amber-900/30 border border-amber-700/40 rounded-xl p-4">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-300 font-semibold text-sm">No dosing data found</p>
              <p className="text-amber-400/70 text-xs mt-1">
                Visit the <button onClick={() => navigate('/chemical-dosing')} className="underline">Chemical Dosing page</button> first to configure your process.
              </p>
            </div>
          </div>
        )}

        {/* ── Process Train Flow Diagram ────────────────────────────────────── */}
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-slate-200">Process Train Overview</h2>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {/* Influent */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className="w-24 h-16 rounded-xl bg-blue-900/40 border-2 border-blue-700/60 flex flex-col items-center justify-center gap-1">
                <div className="text-blue-400"><Beaker className="w-5 h-5" /></div>
                <span className="text-xs text-blue-300 font-semibold">Influent</span>
              </div>
              <span className="text-xs text-slate-500">
                {dosingState ? `${flowQ} m³/h` : '—'}
              </span>
            </div>

            <ChevronRight className="w-5 h-5 text-slate-600 shrink-0" />

            {/* Dosing */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className={`w-32 h-16 rounded-xl flex flex-col items-center justify-center gap-1 border-2 ${
                hasDosingData
                  ? 'bg-green-900/40 border-green-700/60'
                  : 'bg-slate-800/40 border-slate-700/50'
              }`}>
                <div className={hasDosingData ? 'text-green-400' : 'text-slate-500'}>
                  <FlaskConical className="w-5 h-5" />
                </div>
                <span className={`text-xs font-semibold ${hasDosingData ? 'text-green-300' : 'text-slate-500'}`}>
                  Chemical Dosing
                </span>
                {hasDosingData && (
                  <span className="text-xs text-green-400/70">{activeChemicals.length} chemicals</span>
                )}
              </div>
              {hasDosingData && (
                <span className="text-xs text-slate-500">{totalKgh.toFixed(1)} kg/h</span>
              )}
            </div>

            <ChevronRight className="w-5 h-5 text-slate-600 shrink-0" />

            {/* Mixing */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className={`w-32 h-16 rounded-xl flex flex-col items-center justify-center gap-1 border-2 ${
                hasMixingData
                  ? 'bg-amber-900/40 border-amber-700/60'
                  : 'bg-slate-800/40 border-slate-700/50'
              }`}>
                <div className={hasMixingData ? 'text-amber-400' : 'text-slate-500'}>
                  <Waves className="w-5 h-5" />
                </div>
                <span className={`text-xs font-semibold ${hasMixingData ? 'text-amber-300' : 'text-slate-500'}`}>
                  Impeller Mixing
                </span>
                {hasMixingData && mixCfg && (
                  <span className="text-xs text-amber-400/70">
                    {IMPELLER_NAMES[mixCfg.impellerType] ?? mixCfg.impellerType}
                  </span>
                )}
              </div>
              {hasMixingData && mixResults && (
                <span className="text-xs text-slate-500">G = {mixResults.G.toFixed(0)} s⁻¹</span>
              )}
            </div>

            <ChevronRight className="w-5 h-5 text-slate-600 shrink-0" />

            {/* Effluent */}
            <div className="shrink-0 flex flex-col items-center gap-1.5">
              <div className="w-24 h-16 rounded-xl bg-purple-900/40 border-2 border-purple-700/60 flex flex-col items-center justify-center gap-1">
                <div className="text-purple-400"><CheckCircle2 className="w-5 h-5" /></div>
                <span className="text-xs text-purple-300 font-semibold">Effluent</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── KPI Summary ───────────────────────────────────────────────────── */}
        {hasMixingData && mixResults && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPIChip
              label="Velocity Gradient G"
              value={mixResults.G.toFixed(1)}
              unit="s⁻¹"
              color={gColor}
            />
            <KPIChip
              label="Camp Number Gt"
              value={fmtSI(mixResults.Gt)}
              unit=""
              color={gtColor}
            />
            <KPIChip
              label="Mixing Power"
              value={mixResults.P_kW.toFixed(3)}
              unit="kW"
              color="amber"
            />
            <KPIChip
              label="Power Density"
              value={powerDensity.toFixed(3)}
              unit="kW/m³"
              color="amber"
            />
            <KPIChip
              label="HRT"
              value={mixResults.hrt_min.toFixed(1)}
              unit="min"
              color="blue"
            />
            <KPIChip
              label="Mixing Time θm"
              value={mixResults.theta_m.toFixed(1)}
              unit="s"
              color="purple"
            />
          </div>
        )}

        {/* ── Main content grid ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── COLUMN 1: Stage cards ──────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Influent stage */}
            <StageCard title="① Influent" icon={<Beaker className="w-4 h-4" />} color="blue">
              {dosingState ? (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-slate-400 mb-2">
                    <span>Sample: <span className="text-slate-200">{dosingState.influentSampleName}</span></span>
                    <span>Q = <span className="text-slate-200">{flowQ} m³/h</span></span>
                  </div>
                  {Object.entries(PARAMS).map(([k, m]) => {
                    const v = influentParams[k];
                    if (v === undefined || v === 0) return null;
                    return (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-slate-400">{m.label}</span>
                        <span className="text-slate-200 font-mono">{fmt(v, m.decimals)} {m.unit}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-slate-500">No influent data</p>
              )}
            </StageCard>

            {/* Dosing stage */}
            <StageCard title="② Chemical Dosing" icon={<FlaskConical className="w-4 h-4" />} color="green">
              {hasDosingData ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-1.5 mb-2">
                    <div className="bg-slate-800/40 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-400">Active chemicals</div>
                      <div className="text-base font-bold text-emerald-300">{activeChemicals.length}</div>
                    </div>
                    <div className="bg-slate-800/40 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-400">Total dose</div>
                      <div className="text-base font-bold text-emerald-300">{totalKgd.toFixed(0)} kg/d</div>
                    </div>
                  </div>
                  {activeChemicals.map(c => {
                    const kgh = (c.dose / 1000) * flowQ;
                    return (
                      <div key={c.id} className="flex justify-between text-xs bg-slate-800/30 rounded px-2 py-1">
                        <span className="text-slate-300 capitalize">{c.id.replace(/_/g, ' ')}</span>
                        <div className="flex gap-2 text-slate-400">
                          <span>{c.dose} mg/L</span>
                          <span className="text-slate-500">{kgh.toFixed(2)} kg/h</span>
                        </div>
                      </div>
                    );
                  })}
                  {activeChemicals.length === 0 && (
                    <p className="text-xs text-slate-500">No chemicals active</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">No dosing data</p>
              )}
            </StageCard>

            {/* Mixing stage */}
            <StageCard title="③ Impeller Mixing" icon={<Waves className="w-4 h-4" />} color="amber">
              {hasMixingData && mixCfg && mixResults ? (
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Impeller</span>
                    <span className="text-slate-200">{IMPELLER_NAMES[mixCfg.impellerType] ?? mixCfg.impellerType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Speed</span>
                    <span className="text-slate-200">{mixCfg.rpm} RPM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tank</span>
                    <span className="text-slate-200 capitalize">{mixCfg.tankShape} {mixCfg.tankVolume} m³</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Impellers</span>
                    <span className="text-slate-200">{mixCfg.numberOfImpellers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">D/T ratio</span>
                    <span className="text-slate-200">{mixCfg.dtRatio.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">H/D ratio</span>
                    <span className="text-slate-200">{mixCfg.hdRatio.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Baffled</span>
                    <span className="text-slate-200">{mixCfg.baffled ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="pt-1 border-t border-slate-700/40 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Re</span>
                      <span className="text-amber-300 font-mono">{fmtSI(mixResults.Re)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">G value</span>
                      <span className="text-amber-300 font-mono">{mixResults.G.toFixed(1)} s⁻¹</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Power</span>
                      <span className="text-amber-300 font-mono">{mixResults.P_kW.toFixed(3)} kW</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Flow regime</span>
                      <span className="text-amber-300/80 text-xs">{mixResults.flowRegime}</span>
                    </div>
                    <div className={`flex items-center gap-1.5 mt-1 text-xs rounded-lg px-2 py-1 ${
                      mixResults.mixingStatus === 'Optimal'
                        ? 'bg-emerald-900/30 text-emerald-300'
                        : 'bg-amber-900/30 text-amber-300'
                    }`}>
                      {mixResults.mixingStatus === 'Optimal'
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <AlertTriangle className="w-3.5 h-3.5" />}
                      {mixResults.mixingStatus}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 space-y-2">
                  <p>No mixing data found.</p>
                  <button
                    onClick={() => navigate('/impeller-mixing')}
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    Configure mixing →
                  </button>
                </div>
              )}
            </StageCard>
          </div>

          {/* ── COLUMN 2+3: Parameter comparison table ─────────────────────── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Full parameter table */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-amber-400" />
                  <h2 className="text-sm font-semibold text-slate-200">Full Parameter Comparison</h2>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <span className="text-emerald-400">▼</span> removal
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-red-400">▲</span> addition
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800/40">
                      <th className="py-2 pl-3 pr-2 text-left text-xs font-medium text-slate-400">Parameter</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-blue-400">Influent</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-emerald-400">Post-Dosing</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-purple-400">Final Effluent</th>
                      <th className="py-2 pr-3 text-right text-xs font-medium text-slate-400">Overall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(PARAMS).map(([k, m]) => (
                      <ParamRow
                        key={k}
                        label={m.label}
                        unit={m.unit}
                        inlet={influentParams[k]}
                        postDosing={postDosingParams[k]}
                        final={finalParams[k]}
                        decimals={m.decimals}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Removal efficiency summary */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <h2 className="text-sm font-semibold text-slate-200">Removal Efficiency Summary</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(['turbidity', 'tss', 'cod', 'bod', 'tp', 'tn'] as const).map(k => {
                  const meta = PARAMS[k];
                  const inV  = influentParams[k]  ?? 0;
                  const outV = finalParams[k]      ?? 0;
                  const pct  = removalPct(inV, outV);
                  if (inV === 0) return null;
                  return (
                    <div key={k} className="bg-slate-800/40 rounded-xl p-3">
                      <div className="text-xs text-slate-400 mb-1">{meta.label}</div>
                      <div className={`text-xl font-bold font-mono ${
                        pct !== null && pct > 0 ? 'text-emerald-400'
                        : pct !== null && pct < 0 ? 'text-red-400'
                        : 'text-slate-400'
                      }`}>
                        {pct !== null ? `${pct > 0 ? '▼' : '▲'} ${Math.abs(pct).toFixed(1)}%` : '—'}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {fmt(inV, meta.decimals)} → {fmt(outV, meta.decimals)} {meta.unit}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Final effluent card */}
            <div className="bg-gradient-to-br from-purple-950/40 to-slate-900/60 border border-purple-700/40 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-purple-400" />
                <h2 className="text-sm font-semibold text-purple-300">④ Final Effluent Quality</h2>
                <span className="text-xs text-slate-500 ml-1">Post dosing + mixing</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(PARAMS).map(([k, m]) => {
                  const v = finalParams[k];
                  if (v === undefined) return null;
                  return (
                    <div key={k} className="bg-slate-800/50 rounded-lg p-2 text-center">
                      <div className="text-xs text-slate-400">{m.label}</div>
                      <div className="text-sm font-bold text-purple-300 font-mono">
                        {fmt(v, m.decimals)}
                      </div>
                      {m.unit && <div className="text-xs text-slate-500">{m.unit}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendations */}
            {mixResults && (
              <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-4 h-4 text-blue-400" />
                  <h2 className="text-sm font-semibold text-slate-200">Process Recommendations</h2>
                </div>
                <div className="space-y-2">
                  {mixResults.G < 10 && (
                    <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      G &lt; 10 s⁻¹ — mixing intensity too low for effective chemical dispersion. Increase RPM or reduce tank volume.
                    </div>
                  )}
                  {mixResults.G > 1500 && (
                    <div className="flex items-start gap-2 bg-red-900/20 border border-red-700/30 rounded-lg p-3 text-xs text-red-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      G &gt; 1 500 s⁻¹ — excessive shear will break fragile floc. Reduce RPM or use a gentler impeller type (e.g. Hydrofoil, Marine propeller).
                    </div>
                  )}
                  {mixResults.Gt < 1000 && (
                    <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Gt &lt; 1 000 — insufficient contact time. Increase HRT by enlarging tank volume or reducing flow rate.
                    </div>
                  )}
                  {mixResults.Gt > 100000 && (
                    <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-xs text-amber-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Gt &gt; 100 000 — over-mixed. Reduce HRT or G to avoid floc degradation.
                    </div>
                  )}
                  {mixResults.G >= 10 && mixResults.G <= 1500 && mixResults.Gt >= 1000 && mixResults.Gt <= 100000 && (
                    <div className="flex items-start gap-2 bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-3 text-xs text-emerald-300">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Mixing conditions are within optimal range (G = {mixResults.G.toFixed(0)} s⁻¹, Gt = {fmtSI(mixResults.Gt)}). Process is well-configured.
                    </div>
                  )}
                  {activeChemicals.length === 0 && (
                    <div className="flex items-start gap-2 bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 text-xs text-slate-400">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      No chemicals are currently active. Enable chemicals on the Dosing page to model treatment effects.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer actions ────────────────────────────────────────────────── */}
        <div className="flex gap-3 pt-2 pb-4 flex-wrap">
          <button
            onClick={() => navigate('/chemical-dosing')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 text-sm transition-colors"
          >
            <FlaskConical className="w-4 h-4" /> Back to Dosing
          </button>
          <button
            onClick={() => navigate('/impeller-mixing')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 text-sm transition-colors"
          >
            <Waves className="w-4 h-4" /> Back to Mixing
          </button>
          <button
            onClick={() => navigate('/process-train')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-semibold transition-colors ml-auto"
          >
            <LayoutDashboard className="w-4 h-4" /> Full Process Train
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
